import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Warning } from "@phosphor-icons/react";
import { SPECIALTIES } from "@/lib/specialties";
import { normalizeText } from "@/lib/text";

/**
 * Specialty combobox with dropdown ANCHORED BELOW the input (never to the side).
 * Also surfaces an inline error hint when the current value is not one of the listed specialties.
 */
export default function SpecialtyCombobox({ value, onChange, label = "Profession / Spécialité", testid = "account-specialty" }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [touched, setTouched] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = (value ? SPECIALTIES.filter((s) => normalizeText(s).includes(normalizeText(value))) : SPECIALTIES).slice(0, 12);
  const invalid = touched && !!value && !SPECIALTIES.includes(value);

  const pick = (s) => { onChange(s); setOpen(false); setTouched(true); };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(filtered[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <Label className="text-xs tracking-[0.15em] uppercase text-[#5F758F]">{label}</Label>
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTouched(true)}
        onKeyDown={onKey}
        placeholder="Commencez à taper puis choisissez…"
        autoComplete="off"
        className={`mt-1.5 bg-white ${invalid ? "border-red-500 focus-visible:ring-red-500" : "border-[#E1E9F0]"}`}
        data-testid={testid}
      />
      {invalid && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1" data-testid="specialty-error">
          <Warning size={12} weight="duotone" /> Sélectionnez une profession dans la liste proposée.
        </p>
      )}
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E1E9F0] rounded-md shadow-lg max-h-64 overflow-y-auto">
          {filtered.map((s, i) => (
            <li
              key={s}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${active === i ? "bg-[#F4F7FB]" : "hover:bg-[#F4F7FB]"}`}
              data-testid={`specialty-suggestion-${i}`}
            >
              {value === s ? <Check size={14} className="text-[#3A8FA5]" /> : <span className="w-3.5" />}
              <span className="text-[#1E3A5F]">{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
